/**
 * BulkTaskCreator — spreadsheet-style dialog to create many tasks at once.
 *
 * Features:
 *   - Multi-row grid with title, assignee, due date, priority, tags per row
 *   - Batch defaults panel (project, assignee, due date, priority, tags) that
 *     apply to every row without per-row overrides
 *   - Paste-from-CSV: paste CSV text into a textarea to bulk-fill rows.
 *     Header row supports: title, assignee, due_date, priority, tags,
 *     description, status. Missing columns fall back to batch defaults.
 *   - Add / remove rows, clear all
 *   - Server-side POST to /workspaces/{ws}/tasks/bulk-create
 */
import { useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, ClipboardPaste, Send, Copy, X } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const STATUSES = ["pending_approval", "open", "in_progress", "review", "blocked", "done"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const emptyRow = () => ({
  title: "", description: "", assignee_id: "", due_date: "",
  priority: "", status: "", tags: "",
});

export default function BulkTaskCreator({ open, onClose, wsId, projects = [],
                                            members = [], onCreated,
                                            presetDefaults = {} }) {
  const [defaults, setDefaults] = useState({
    project_id: presetDefaults.project_id || "",
    assignee_id: presetDefaults.assignee_id || "",
    due_date: presetDefaults.due_date || "",
    priority: presetDefaults.priority || "medium",
    status: presetDefaults.status || "open",
    tags: presetDefaults.tags || "",
  });
  const [rows, setRows] = useState(() => Array.from({ length: 5 }, emptyRow));
  const [showCsv, setShowCsv] = useState(false);
  const [csvText, setCsvText] = useState("");
  // R148n · Feb 2026 — Long-text mode: one task title per line.
  const [showLongText, setShowLongText] = useState(false);
  const [longText, setLongText] = useState("");
  const [busy, setBusy] = useState(false);

  const setRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => setRows(Array.from({ length: 5 }, emptyRow));

  const parseCsv = () => {
    // Simple CSV parser (no quoted-field commas). Header on line 1 optional.
    const text = (csvText || "").trim();
    if (!text) return;
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return;
    // Detect header: if first row contains a known keyword, treat as header.
    const firstLower = lines[0].toLowerCase();
    const hasHeader = /title|task|name/.test(firstLower.split(",")[0]);
    let header = ["title", "assignee", "due_date", "priority", "tags",
                  "description", "status"];
    let dataLines = lines;
    if (hasHeader) {
      header = lines[0].split(",").map((h) => h.trim().toLowerCase());
      dataLines = lines.slice(1);
    }
    const memberByEmail = new Map();
    const memberByName = new Map();
    members.forEach((m) => {
      if (m.user?.email) memberByEmail.set(m.user.email.toLowerCase(), m.user_id);
      if (m.user?.name) memberByName.set(m.user.name.toLowerCase(), m.user_id);
    });
    const parsed = dataLines.map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const rec = emptyRow();
      header.forEach((col, i) => {
        const v = cells[i] || "";
        if (!v) return;
        if (col === "title" || col === "task" || col === "name") rec.title = v;
        else if (col === "description" || col === "desc") rec.description = v;
        else if (col === "assignee" || col === "assignee_email"
                 || col === "assignee_name") {
          // Resolve to user_id from email or name
          const uid = memberByEmail.get(v.toLowerCase())
            || memberByName.get(v.toLowerCase()) || "";
          rec.assignee_id = uid;
        } else if (col === "assignee_id") rec.assignee_id = v;
        else if (col === "due" || col === "due_date") rec.due_date = v;
        else if (col === "priority") rec.priority = v.toLowerCase();
        else if (col === "status") rec.status = v.toLowerCase();
        else if (col === "tags") rec.tags = v;
      });
      return rec;
    });
    setRows(parsed.length ? parsed : Array.from({ length: 5 }, emptyRow));
    setShowCsv(false);
    setCsvText("");
    toast.success(`Loaded ${parsed.length} row${parsed.length === 1 ? "" : "s"} from CSV`);
  };

  const validRows = useMemo(
    () => rows.filter((r) => (r.title || "").trim().length > 0),
    [rows]
  );

  // R148n · Feb 2026 — Long-text mode: split by newline, one title per line.
  //  Common fields are the batch defaults already at the top of the dialog
  //  so users only have to fill defaults once and paste titles below.
  const parseLongText = () => {
    const lines = (longText || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) { toast.info("Paste at least one line"); return; }
    setRows(lines.map((title) => ({ ...emptyRow(), title })));
    setShowLongText(false);
    setLongText("");
    toast.success(`Loaded ${lines.length} title${lines.length === 1 ? "" : "s"} from text`);
  };

  const submit = async () => {
    if (validRows.length === 0) {
      toast.error("Add at least one task title to save");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        defaults: {
          project_id: defaults.project_id || undefined,
          assignee_id: defaults.assignee_id || undefined,
          due_date: defaults.due_date || undefined,
          priority: defaults.priority || undefined,
          status: defaults.status || undefined,
          tags: defaults.tags
            ? defaults.tags.split(",").map((t) => t.trim()).filter(Boolean)
            : undefined,
        },
        rows: validRows.map((r) => ({
          title: r.title.trim(),
          description: r.description || undefined,
          assignee_id: r.assignee_id || undefined,
          due_date: r.due_date || undefined,
          priority: r.priority || undefined,
          status: r.status || undefined,
          tags: r.tags
            ? r.tags.split(",").map((t) => t.trim()).filter(Boolean)
            : undefined,
        })),
      };
      const { data } = await api.post(`/workspaces/${wsId}/tasks/bulk-create`,
                                        payload);
      toast.success(`Created ${data.created_count} tasks`);
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create tasks"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="bulk-task-creator">
        <DialogHeader>
          <DialogTitle>Bulk task creator</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Paste from a spreadsheet, type directly, or import CSV. Per-row values override the batch defaults.
          </p>
        </DialogHeader>

        {/* Batch defaults */}
        <div className="border border-border bg-muted/30 p-3 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="btc-defaults">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Project</Label>
            <Select value={defaults.project_id || "_none"}
                    onValueChange={(v) => setDefaults({ ...defaults, project_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="btc-defaults-project"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Assignee</Label>
            <Select value={defaults.assignee_id || "_none"}
                    onValueChange={(v) => setDefaults({ ...defaults, assignee_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="btc-defaults-assignee"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Unassigned</SelectItem>
                {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date</Label>
            <Input type="date" value={defaults.due_date}
                   onChange={(e) => setDefaults({ ...defaults, due_date: e.target.value })}
                   className="h-8 text-xs" data-testid="btc-defaults-due" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Priority</Label>
            <Select value={defaults.priority}
                    onValueChange={(v) => setDefaults({ ...defaults, priority: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="btc-defaults-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</Label>
            <Select value={defaults.status}
                    onValueChange={(v) => setDefaults({ ...defaults, status: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="btc-defaults-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tags (comma-sep)</Label>
            <Input value={defaults.tags}
                   onChange={(e) => setDefaults({ ...defaults, tags: e.target.value })}
                   className="h-8 text-xs" placeholder="e.g. sprint-14,ux"
                   data-testid="btc-defaults-tags" />
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 border-b border-border pb-2">
          <Button size="sm" variant="outline" className="h-8 rounded-none" onClick={addRow} data-testid="btc-add-row">
            <Plus className="w-3 h-3 mr-1" /> Add row
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-none"
                  onClick={() => setShowCsv((v) => !v)} data-testid="btc-toggle-csv">
            <ClipboardPaste className="w-3 h-3 mr-1" /> {showCsv ? "Hide CSV" : "Paste from CSV"}
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-none"
                  onClick={() => setShowLongText((v) => !v)} data-testid="btc-toggle-longtext">
            <ClipboardPaste className="w-3 h-3 mr-1" /> {showLongText ? "Hide long-text" : "Paste titles (one per line)"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 rounded-none" onClick={clearAll} data-testid="btc-clear">
            <X className="w-3 h-3 mr-1" /> Reset
          </Button>
          <span className="text-xs text-muted-foreground ml-auto" data-testid="btc-count">
            {validRows.length} of {rows.length} row{rows.length === 1 ? "" : "s"} ready
          </span>
        </div>

        {showLongText && (
          <div className="border border-dashed border-primary/40 p-3 space-y-2 bg-primary/5" data-testid="btc-longtext-panel">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Copy className="w-3 h-3" />
              Long-text mode — paste one task title per line. The batch defaults above (project, assignee, due, priority, tags, status) apply to every line.
            </div>
            <Textarea
              rows={8}
              placeholder={"Wire up the auth screen\nDraft the launch email\nReview PR 42\nBook the vendor demo"}
              value={longText}
              onChange={(e) => setLongText(e.target.value)}
              data-testid="btc-longtext-textarea"
              className="font-mono text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setLongText(""); setShowLongText(false); }}>Cancel</Button>
              <Button size="sm" onClick={parseLongText} data-testid="btc-longtext-load">
                Load {longText.split(/\r?\n/).filter((l) => l.trim()).length} tasks
              </Button>
            </div>
          </div>
        )}

        {showCsv && (
          <div className="border border-dashed border-border p-3 space-y-2 bg-muted/20" data-testid="btc-csv-panel">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Copy className="w-3 h-3" />
              CSV import — accepts columns: title, description, assignee (name or email), due_date, priority, status, tags
            </div>
            <Textarea
              rows={6}
              placeholder={"title,assignee,due_date,priority,tags\nWrite spec,alice@example.com,2026-03-01,high,api,docs\nReview PR,bob@example.com,2026-03-02,medium,review"}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              data-testid="btc-csv-textarea"
              className="font-mono text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setCsvText(""); setShowCsv(false); }}>Cancel</Button>
              <Button size="sm" onClick={parseCsv} data-testid="btc-csv-load">Load rows</Button>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="overflow-auto flex-1 border border-border" data-testid="btc-grid">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr className="text-left">
                <th className="p-2 border-b border-border w-8">#</th>
                <th className="p-2 border-b border-border">Title *</th>
                <th className="p-2 border-b border-border w-44">Assignee</th>
                <th className="p-2 border-b border-border w-32">Due</th>
                <th className="p-2 border-b border-border w-24">Priority</th>
                <th className="p-2 border-b border-border w-36">Tags</th>
                <th className="p-2 border-b border-border w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-b border-border" data-testid={`btc-row-${idx}`}>
                  <td className="p-2 text-muted-foreground">{idx + 1}</td>
                  <td className="p-1">
                    <Input value={r.title} onChange={(e) => setRow(idx, { title: e.target.value })}
                           placeholder="Task title"
                           className="h-8 text-xs" data-testid={`btc-row-${idx}-title`} />
                  </td>
                  <td className="p-1">
                    <Select value={r.assignee_id || "_batch"} onValueChange={(v) => setRow(idx, { assignee_id: v === "_batch" ? "" : v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`btc-row-${idx}-assignee`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_batch">Use batch default</SelectItem>
                        {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-1">
                    <Input type="date" value={r.due_date}
                           onChange={(e) => setRow(idx, { due_date: e.target.value })}
                           className="h-8 text-xs" data-testid={`btc-row-${idx}-due`} />
                  </td>
                  <td className="p-1">
                    <Select value={r.priority || "_batch"} onValueChange={(v) => setRow(idx, { priority: v === "_batch" ? "" : v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`btc-row-${idx}-priority`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_batch">Batch</SelectItem>
                        {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-1">
                    <Input value={r.tags}
                           onChange={(e) => setRow(idx, { tags: e.target.value })}
                           placeholder="tag1, tag2"
                           className="h-8 text-xs" data-testid={`btc-row-${idx}-tags`} />
                  </td>
                  <td className="p-1 text-right">
                    <button type="button" onClick={() => removeRow(idx)} className="text-muted-foreground hover:text-rose-600"
                            data-testid={`btc-row-${idx}-remove`}
                            aria-label="Remove row">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" onClick={onClose} data-testid="btc-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || validRows.length === 0} data-testid="btc-submit">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {busy ? "Creating..." : `Create ${validRows.length} task${validRows.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
